import { totalProjectedMoneyFlow, type ProgramQuote } from '@hopdrive/sdk-ar';

import type { ProgramVisualsConfig } from '@features/programs/programVisuals';

/**
 * Turns a `quoteProgram` result into the display lines for the move planner's
 * "Program Coverage" estimate — the live, pre-scenario mirror of the persisted
 * invoice breakdown. Pure (no React, no SDK calls) so the mapping is
 * unit-testable; the component is a thin renderer over it.
 *
 * `quoteProgram` returns null when the customer isn't enrolled, so a non-null
 * quote already means "enrolled" — the display gate here is only the
 * per-customer `program_visuals` flag. The projected money is already
 * eligibility-gated inside quoteProgram (ineligible ⇒ $0 program coverage), so
 * this layer just maps figures per settlement mode. Why a vehicle is
 * ineligible is worded in `programVehicleEligibility.ts`.
 */

// Settlement mode values, mirrored from sdk-ar's SETTLEMENT_MODE (not exported
// at the package index). PROGRAM_FRONTS/SPLIT are full-service; REIMBURSEMENT
// is the lite-tier (self-perform) mode.
const SETTLEMENT = {
  PROGRAM_FRONTS: 'program_fronts',
  SPLIT: 'split',
  REIMBURSEMENT: 'reimbursement',
} as const;

export interface ProgramCoverageLines {
  settlement: string;
  /** What the move costs at the program's rates, before the split. The planner strip shows this as the price. */
  moveCost: number;
  /** Program's NET covered share (what it actually pays) under the billing modes, fronts and split; a reimbursed move uses `reimbursement` instead. */
  covered: number;
  /** Program → dealer reimbursement (lite only). */
  reimbursement: number;
  /** Dealer → program recoup (fronts only; gated by show_recoup). */
  recoup: number;
  /** Dealer's own share paid to HopDrive (split only). */
  youPay: number;
  showCoveredLine: boolean;
  showReimbursementLine: boolean;
  showRecoupLine: boolean;
  showYouPayLine: boolean;
}

/**
 * The coverage lines to show for a quote, or null when nothing should render
 * (no quote / customer not enrolled, or the program_visuals flag is off).
 */
export function buildProgramCoverageLines(
  quote: ProgramQuote | null | undefined,
  visuals: ProgramVisualsConfig,
): ProgramCoverageLines | null {
  if (!quote || visuals.active !== true) return null;

  const flow = quote.projectedMoneyFlow;
  // The move cost plus everything quoted beside it (the lane toll, and any
  // accessorials), so the card shows what the move as a whole costs and who pays.
  const totals = totalProjectedMoneyFlow(flow);
  const isReimbursement = flow.settlement === SETTLEMENT.REIMBURSEMENT;
  const isSplit = flow.settlement === SETTLEMENT.SPLIT;

  return {
    settlement: flow.settlement,
    moveCost: totals.totalCost,
    // NET covered share the program actually pays — programAmount, NOT the gross
    // programCharge (which under fronting is the full cost the program fronts and
    // then recoups, so it reads $100 whether the vehicle is eligible or not, and
    // makes the estimate look frozen when the make/eligibility changes).
    covered: totals.programAmount,
    reimbursement: totals.reimbursement,
    recoup: totals.recoup,
    youPay: totals.customerCharge,
    showCoveredLine: !isReimbursement,
    showReimbursementLine: isReimbursement,
    showRecoupLine: !isReimbursement && visuals.show_recoup === true && totals.recoup > 0,
    showYouPayLine: isSplit && totals.customerCharge > 0,
  };
}

// Under fronting the program pays HopDrive and the dealer pays the program
// back; HopDrive never invoices the dealer for it. "May" because this is an
// estimate, and the figure is only settled once the move is. Under split the
// dealer's share is a HopDrive invoice line.
export const RECOUP_LABEL = 'You may owe';
export const YOUR_SHARE_LABEL = 'Your share, billed by HopDrive';

/**
 * The one figure a collapsed coverage bar headlines: what the dealer most
 * wants, their own cost. Under fronting that's what they owe the program;
 * under split it's their direct share; both can't be non-zero at once. Lite is
 * the exception: the program pays the dealer, so the headline is the
 * reimbursement (money coming in), not a cost. The move cost itself shows on
 * the lane estimate strip. Both planner boxes headline through this, so they
 * can't name different figures.
 */
export function summarizeProgramCoverage(lines: ProgramCoverageLines): { label: string; value: number } {
  if (lines.showReimbursementLine) return { label: 'Reimbursement', value: lines.reimbursement };
  if (lines.showYouPayLine) return { label: YOUR_SHARE_LABEL, value: lines.youPay };
  return { label: RECOUP_LABEL, value: lines.recoup };
}
